import { useState, useEffect, useCallback } from 'react';
import api, { formatPhone, formatDateTime } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Play,
  Pause,
  Gear,
  Phone,
  Clock,
  Lightning,
  ArrowClockwise
} from '@phosphor-icons/react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function Queue() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    maxConcurrent: 1,
    delayBetween: 30,
    maxRetries: 3,
    operatingHoursStart: '09:00',
    operatingHoursEnd: '17:00',
    operatingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  });

  const loadStatus = useCallback(async () => {
    try {
      const response = await api.get('/queue/status');
      setStatus(response.data);
      setSettings(response.data.settings);
    } catch (error) {
      toast.error('Failed to load queue status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, [loadStatus]);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await api.post('/queue/start');
      toast.success('Queue started');
      loadStatus();
    } catch (error) {
      toast.error('Failed to start queue');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    setActionLoading(true);
    try {
      await api.post('/queue/pause');
      toast.success('Queue paused');
      loadStatus();
    } catch (error) {
      toast.error('Failed to pause queue');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/queue/settings', settings);
      toast.success('Settings saved');
      setShowSettings(false);
      loadStatus();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const isWithinOperatingHours = () => {
    if (!settings) return false;
    const now = new Date();
    const dayName = now.toLocaleDateString('en-US', { weekday: 'long' });
    const timeStr = now.toTimeString().substring(0, 5);
    
    if (!settings.operatingDays?.includes(dayName)) return false;
    return timeStr >= settings.operatingHoursStart && timeStr <= settings.operatingHoursEnd;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-zinc-400">Loading queue...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="queue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-medium text-zinc-50 tracking-tight">Call Queue</h1>
          <p className="text-zinc-400 mt-1">Manage automated outbound calling</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="btn-secondary flex items-center gap-2"
            data-testid="queue-settings-btn"
          >
            <Gear size={18} /> Settings
          </button>
          {status?.isRunning ? (
            <button
              onClick={handlePause}
              disabled={actionLoading}
              className="btn-secondary flex items-center gap-2 text-yellow-400"
              data-testid="pause-queue-btn"
            >
              <Pause size={18} weight="fill" /> Pause Queue
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={actionLoading}
              className="btn-primary flex items-center gap-2"
              data-testid="start-queue-btn"
            >
              <Play size={18} weight="fill" /> Start Queue
            </button>
          )}
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-3 h-3 rounded-full ${status?.isRunning ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-500'}`} />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Status</span>
          </div>
          <div className="text-2xl font-medium text-zinc-50">
            {status?.isRunning ? 'Running' : 'Paused'}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <Phone size={18} className="text-zinc-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Queue Length</span>
          </div>
          <div className="text-2xl font-mono text-zinc-50" data-testid="queue-length">
            {status?.queueLength || 0}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <Lightning size={18} className="text-zinc-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Currently Calling</span>
          </div>
          <div className="text-2xl font-medium text-zinc-50">
            {status?.currentLeadId ? 'Yes' : 'No'}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <Clock size={18} className="text-zinc-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Operating Hours</span>
          </div>
          <div className={`text-lg font-medium ${isWithinOperatingHours() ? 'text-emerald-400' : 'text-zinc-500'}`}>
            {isWithinOperatingHours() ? 'Active' : 'Outside Hours'}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {settings?.operatingHoursStart} - {settings?.operatingHoursEnd}
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="card" data-testid="settings-panel">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">Queue Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <Label className="text-zinc-400">Max Concurrent Calls</Label>
              <Select
                value={String(settings.maxConcurrent)}
                onValueChange={(v) => setSettings({ ...settings, maxConcurrent: parseInt(v) })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(n => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400">Delay Between Calls (seconds)</Label>
              <Input
                type="number"
                value={settings.delayBetween}
                onChange={(e) => setSettings({ ...settings, delayBetween: parseInt(e.target.value) })}
                min={10}
                max={120}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Max Retries on No Answer</Label>
              <Select
                value={String(settings.maxRetries)}
                onValueChange={(v) => setSettings({ ...settings, maxRetries: parseInt(v) })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4, 5].map(n => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400">Operating Hours Start</Label>
              <Input
                type="time"
                value={settings.operatingHoursStart}
                onChange={(e) => setSettings({ ...settings, operatingHoursStart: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Operating Hours End</Label>
              <Input
                type="time"
                value={settings.operatingHoursEnd}
                onChange={(e) => setSettings({ ...settings, operatingHoursEnd: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Operating Days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DAYS.map(day => (
                  <button
                    key={day}
                    onClick={() => {
                      const days = settings.operatingDays || [];
                      if (days.includes(day)) {
                        setSettings({ ...settings, operatingDays: days.filter(d => d !== day) });
                      } else {
                        setSettings({ ...settings, operatingDays: [...days, day] });
                      }
                    }}
                    className={`px-2 py-1 text-xs rounded-sm transition-colors ${
                      settings.operatingDays?.includes(day)
                        ? 'bg-blue-600 text-white'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {day.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-zinc-800">
            <Button variant="outline" onClick={() => setShowSettings(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-500">
              Save Settings
            </Button>
          </div>
        </div>
      )}

      {/* Queued Leads */}
      <div className="card">
        <h3 className="text-lg font-medium text-zinc-50 mb-4">Queued Leads</h3>
        {status?.queuedLeads?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Priority</th>
                  <th>Queued At</th>
                </tr>
              </thead>
              <tbody>
                {status.queuedLeads.map((lead) => (
                  <tr key={lead.id} data-testid={`queued-lead-${lead.id}`}>
                    <td className="font-medium text-zinc-50">
                      {lead.firstName} {lead.lastName}
                    </td>
                    <td className="font-mono text-zinc-400">{formatPhone(lead.phone)}</td>
                    <td>
                      <span className={`badge ${
                        lead.priority === 'high' ? 'badge-error' : 
                        lead.priority === 'medium' ? 'badge-warning' : 'badge-neutral'
                      }`}>
                        {lead.priority}
                      </span>
                    </td>
                    <td className="text-zinc-500">{formatDateTime(lead.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-zinc-500">
            <Phone size={48} className="mx-auto mb-4 opacity-50" />
            <p>No leads in queue</p>
            <p className="text-sm mt-1">Add leads and set their status to "queued" to start calling</p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <h4 className="text-sm font-medium text-zinc-50 mb-2">How the Queue Works</h4>
        <ul className="text-sm text-zinc-400 space-y-1 list-disc list-inside">
          <li>Leads with status "queued" are processed in priority order (high → medium → low)</li>
          <li>The queue respects operating hours and won't make calls outside that window</li>
          <li>If a call goes unanswered, it will be retried up to {settings?.maxRetries || 3} times</li>
          <li>There's a {settings?.delayBetween || 30} second delay between calls</li>
        </ul>
      </div>
    </div>
  );
}
